import { useEffect, useState } from 'react'
import { cn } from '../../lib/cn'
import { DrawShell } from './DrawScreen'
import type { DrawView } from '../admin/screenTypes'

/**
 * The presentation cheque, on the Grand Draw's own screen.
 *
 * It is deliberately **not** a stage of its own. The Committee's note was that
 * the cheque is the next beat of the Grand Lucky Draw — the winner has just
 * been named, and now the cheque comes on for the photograph — so it keeps the
 * draw's frame: the same border, the same flags, the same title across the top.
 * Only the winner's card is gone, because the cheque carries the name itself.
 *
 * What changes is the arrival: the cheque is pushed up from below the bottom
 * edge rather than appearing in place, and the name is written on afterwards.
 */

/** Native size of `cheque.png`, so the box holds the artwork's proportions. */
const ART = { width: 1842, height: 854 } as const

/**
 * Where the name sits, as percentages of the artwork.
 *
 * Measured against the printed PAY rule. **These are the numbers to nudge** if
 * the name sits high, low or wide of the line — nothing else here needs to
 * change, and `cqw` keeps them true at any projector size.
 */
const NAME = {
  left: 11.5,
  bottom: 58,
  width: 52,
  size: 3.0,
} as const

/**
 * How long the cheque takes to arrive, matching `.cheque-in` in the stylesheet.
 *
 * The name is written on after it lands, not while it is still moving: a pen
 * stroke on a board that is sliding reads as a glitch from the back of a hall.
 */
const ARRIVAL_MS = 1150

export function ChequeScreen({ draw }: { draw: DrawView | undefined }) {
  /*
   * The Grand Draw has one winner, and an absent one is not the winner — the
   * same rule the draw screen follows. If the Committee has marked them
   * absent and not yet re-drawn, the cheque waits rather than putting a name
   * on a projector that has already been called three times.
   */
  const winner = draw?.winners.find((w) => !w.absent) ?? null

  /** Written on after the cheque has settled, not as it flies in. */
  const [written, setWritten] = useState(false)

  useEffect(() => {
    if (!winner) return
    setWritten(false)
    const t = setTimeout(() => setWritten(true), ARRIVAL_MS + 150)
    return () => clearTimeout(t)
  }, [winner?.name])

  return (
    <DrawShell
      theme="gold"
      title={draw?.name ?? 'Grand Lucky Draw'}
      status={{ tone: 'amber', text: winner ? 'Presentation' : 'Awaiting the grand winner' }}
    >
      <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center py-[1.6vmin]">
        {!winner ? (
          <p className="text-[3.4vmin] font-bold uppercase tracking-[0.3em] text-white/50">
            Awaiting the Grand Draw
          </p>
        ) : (
          <div
            /*
              Width-driven, never `h-full`: the height comes from the artwork's
              own ratio, and pinning both would squash the cheque — which also
              moves the printed PAY rule out from under the name.
            */
            className="cheque-in relative w-full max-w-[150vmin]"
            style={{
              aspectRatio: `${ART.width} / ${ART.height}`,
              /* Makes `cqw` below mean "percent of the cheque", not of the screen. */
              containerType: 'inline-size',
            }}
          >
            <img
              src="/cheque-new.png"
              alt=""
              className="absolute inset-0 h-full w-full object-contain drop-shadow-[0_2vmin_6vmin_rgba(0,0,0,0.55)]"
              /* Decorative: the winner's name is announced in text below. */
              aria-hidden
            />

            <div
              className="absolute"
              style={{
                left: `${NAME.left}%`,
                bottom: `${NAME.bottom}%`,
                width: `${NAME.width}%`,
              }}
            >
              <span
                className={cn(
                  'block truncate font-serif italic leading-tight pb-[0.2em] text-[#14213D]',
                  /* Wipe left → right, like the cheque is being filled in. */
                  'transition-[clip-path,opacity] duration-[1000ms] ease-out will-change-[clip-path,opacity]',
                )}
                style={{
                  fontSize: `${NAME.size}cqw`,
                  clipPath: written ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)',
                  opacity: written ? 1 : 0,
                }}
              >
                {winner.name}
              </span>
            </div>

            {/* The name as text, for anything reading the page rather than seeing it. */}
            <span className="sr-only">Grand Lucky Draw winner: {winner.name}</span>
          </div>
        )}
      </div>
    </DrawShell>
  )
}
